import { z } from 'zod'

/**
 * AST ของเงื่อนไขขั้นสูง
 *
 * ข้อความที่ผู้ใช้พิมพ์ (`source`) ถูก parse เป็น AST **ตอนแก้ไขในหน้า builder เท่านั้น**
 * ตอนรันจริงระบบประเมินจาก AST ล้วน ไม่มีการ parse string — จึงไม่มีทางพังเพราะ
 * ข้อความเพี้ยนหลังบันทึก และ error ทางไวยากรณ์ถูกจับตั้งแต่ตอนออกแบบ
 */

/** อ้างถึงฟิลด์คำตอบ: Q3 (ฟิลด์แรกของขั้นตอน) หรือ Q3.storeName */
export type FieldRef = { nodeKey: string; fieldName?: string }

export type CompareOp = 'eq' | 'ne' | 'lt' | 'lte' | 'gt' | 'gte' | 'contains'

export type ExprAst =
  | { kind: 'and'; left: ExprAst; right: ExprAst }
  | { kind: 'or'; left: ExprAst; right: ExprAst }
  | { kind: 'not'; expr: ExprAst }
  | { kind: 'compare'; ref: FieldRef; op: CompareOp; value: string | number }
  | { kind: 'answered'; ref: FieldRef }
  | { kind: 'literal'; value: boolean }

export const FieldRefSchema = z.strictObject({
  nodeKey: z.string().min(1),
  fieldName: z.string().min(1).optional(),
})

export const COMPARE_OPS = ['eq', 'ne', 'lt', 'lte', 'gt', 'gte', 'contains'] as const

export const ExprAstSchema: z.ZodType<ExprAst> = z.lazy(() =>
  z.union([
    z.strictObject({ kind: z.literal('and'), left: ExprAstSchema, right: ExprAstSchema }),
    z.strictObject({ kind: z.literal('or'), left: ExprAstSchema, right: ExprAstSchema }),
    z.strictObject({ kind: z.literal('not'), expr: ExprAstSchema }),
    z.strictObject({
      kind: z.literal('compare'),
      ref: FieldRefSchema,
      op: z.enum(COMPARE_OPS),
      value: z.union([z.string(), z.number()]),
    }),
    z.strictObject({ kind: z.literal('answered'), ref: FieldRefSchema }),
    z.strictObject({ kind: z.literal('literal'), value: z.boolean() }),
  ]),
)

/** เดินทั่ว AST เพื่อเก็บทุก field ที่ถูกอ้างถึง (ใช้ตรวจความสมบูรณ์ของ flow) */
export function collectExprRefs(ast: ExprAst, out: FieldRef[] = []): FieldRef[] {
  switch (ast.kind) {
    case 'and':
    case 'or':
      collectExprRefs(ast.left, out)
      collectExprRefs(ast.right, out)
      break
    case 'not':
      collectExprRefs(ast.expr, out)
      break
    case 'compare':
    case 'answered':
      out.push(ast.ref)
      break
    case 'literal':
      break
  }
  return out
}
